import * as types from '../mutation-types'
import moment from 'moment'

function defaultMarketing() {
	return {
		visitNotes: null,
		coreMessage: null,
		// maps to the visits/create payload's `nextVisitNotes` field (matches VisitDialog.vue's `centerNotes`)
		centerNotes: null,
		objection: null,
		nextCall: null,
		unavailableMaterialIds: [],
		residentDoctorNames: [],
		competitors: [],
		otherSamples: null,
	}
}

export default {
	namespaced: true,

	state: {
		// id of the server visit being viewed/edited; null while creating a new visit
		id: null,
		customerTypeId: null,
		entityId: null,
		// kept alongside the id because the header can't count on the entity being in the
		// entities store: that list is filled by a bulk boot fetch that may still be in
		// flight (or have failed), so the id alone leaves the name blank
		entityName: null,
		visitDate: null,
		priceTypeId: null,
		durationInMinutes: null,
		invoiceItems: [],
		// sent to visits/create|update as materialAmounts entries with returnedTransaction: true —
		// the picked reason stays UI-only, the backend has no field for it
		returnItems: [],
		// id of the order already attached to the visit being edited, so saving updates it
		// instead of posting a second order for the same visit; null while creating
		orderId: null,
		orderItems: [],
		orderDeliveryDate: null,
		orderDeliveryTime: null,
		orderDeliveryPlace: null,
		marketing: defaultMarketing(),
		payments: [],
		// UI-only for now, not sent to visits/create|update — no backend field exists
		discountAmount: null,
		// { [materialId]: amount } snapshot of the visit being edited's own claimed amounts,
		// so its own materials don't count against its own remaining-amount validation
		originalMaterialAmounts: {}
	},

	actions: {
		setCustomer({ commit }, { customerTypeId, entityId, entityName = null, visitDate }) {
			commit(types.STORE_VISIT_DRAFT_CUSTOMER, { customerTypeId, entityId, entityName, visitDate })
		},

		setPriceType({ commit }, priceTypeId) {
			commit(types.STORE_VISIT_DRAFT_PRICE_TYPE, priceTypeId)
		},

		setDuration({ commit }, durationInMinutes) {
			commit(types.STORE_VISIT_DRAFT_DURATION, durationInMinutes)
		},

		setInvoiceItems({ commit }, invoiceItems) {
			commit(types.STORE_VISIT_DRAFT_INVOICE_ITEMS, invoiceItems)
		},

		setReturnItems({ commit }, returnItems) {
			commit(types.STORE_VISIT_DRAFT_RETURN_ITEMS, returnItems)
		},

		setOrderItems({ commit }, orderItems) {
			commit(types.STORE_VISIT_DRAFT_ORDER_ITEMS, orderItems)
		},

		// hydrates the whole order at once from the one already saved against the visit
		setOrder({ commit }, order) {
			commit(types.STORE_VISIT_DRAFT_ORDER, order)
		},

		setOrderDeliveryDate({ commit }, orderDeliveryDate) {
			commit(types.STORE_VISIT_DRAFT_ORDER_DELIVERY_DATE, orderDeliveryDate)
		},

		setOrderDeliveryTime({ commit }, orderDeliveryTime) {
			commit(types.STORE_VISIT_DRAFT_ORDER_DELIVERY_TIME, orderDeliveryTime)
		},

		setOrderDeliveryPlace({ commit }, orderDeliveryPlace) {
			commit(types.STORE_VISIT_DRAFT_ORDER_DELIVERY_PLACE, orderDeliveryPlace)
		},

		setMarketing({ commit }, marketing) {
			commit(types.STORE_VISIT_DRAFT_MARKETING, marketing)
		},

		setPayments({ commit }, payments) {
			commit(types.STORE_VISIT_DRAFT_PAYMENTS, payments)
		},

		setDiscountAmount({ commit }, discountAmount) {
			commit(types.STORE_VISIT_DRAFT_DISCOUNT, discountAmount)
		},

		reset({ commit }) {
			commit(types.RESET_VISIT_DRAFT)
		},

		// hydrates the draft from an existing server visit, reversing the mapping
		// AddVisitPayment.vue's submit() does when building the visits/create|update payload
		loadFromVisit({ commit, rootGetters }, visit) {
			const getMaterialByName = rootGetters['materials/getMaterialByName']

			const invoiceItems = (visit.materialAmounts || [])
				.filter(materialAmount => !materialAmount.returnedTransaction)
				.map((materialAmount, index) => ({
					id: index + 1,
					data: {
						materialId: materialAmount.materialId,
						amount: materialAmount.amount,
						price: materialAmount.sellPrice,
						discount: materialAmount.priceDiscount
					}
				}))

			const returnItems = (visit.materialAmounts || [])
				.filter(materialAmount => materialAmount.returnedTransaction)
				.map((materialAmount, index) => ({
					id: index + 1,
					data: {
						materialId: materialAmount.materialId,
						amount: materialAmount.amount,
						price: materialAmount.sellPrice,
						reason: materialAmount.state
					}
				}))

			const originalMaterialAmounts = (visit.materialAmounts || []).reduce((amounts, materialAmount) => {
				amounts[materialAmount.materialId] = (amounts[materialAmount.materialId] || 0) + materialAmount.amount
				return amounts
			}, {})

			const unavailableMaterialIds = (visit.unavailableMaterials || [])
				.map(name => getMaterialByName(name)?.id)
				.filter(Boolean)

			const residentDoctorNames = (visit.residentDoctorNames || '')
				.split(',')
				.map(name => name.trim())
				.filter(Boolean)
				.map((name, index) => ({ id: index + 1, name }))

			const competitors = (visit.competitiveSamples || [])
				.map((value, index) => ({ id: index + 1, value }))

			commit(types.STORE_VISIT_DRAFT_FROM_VISIT, {
				id: visit.id,
				customerTypeId: visit.entity?.entityTypeId ?? null,
				entityId: visit.entityId,
				entityName: visit.entity?.name ?? null,
				visitDate: moment(visit.createdAt).format('YYYY-MM-DD'),
				priceTypeId: visit.priceTypeId,
				durationInMinutes: visit.durationInMinutes,
				invoiceItems,
				returnItems,
				originalMaterialAmounts,
				marketing: {
					visitNotes: visit.notes,
					coreMessage: visit.coreMessage,
					centerNotes: visit.nextVisitNotes,
					objection: visit.objections,
					nextCall: visit.purpose,
					unavailableMaterialIds,
					residentDoctorNames,
					competitors,
					otherSamples: visit.otherSamples
				},
				payments: [{ id: 1, data: { paymentMethodId: visit.payment, amount: visit.amountReceived } }]
			})
		}
	},

	mutations: {
		[types.STORE_VISIT_DRAFT_CUSTOMER](state, { customerTypeId, entityId, entityName, visitDate }) {
			state.customerTypeId = customerTypeId
			state.entityId = entityId
			state.entityName = entityName
			state.visitDate = visitDate
		},

		[types.STORE_VISIT_DRAFT_PRICE_TYPE](state, priceTypeId) {
			state.priceTypeId = priceTypeId
		},

		[types.STORE_VISIT_DRAFT_DURATION](state, durationInMinutes) {
			state.durationInMinutes = durationInMinutes
		},

		[types.STORE_VISIT_DRAFT_INVOICE_ITEMS](state, invoiceItems) {
			state.invoiceItems = invoiceItems
		},

		[types.STORE_VISIT_DRAFT_RETURN_ITEMS](state, returnItems) {
			state.returnItems = returnItems
		},

		[types.STORE_VISIT_DRAFT_ORDER_ITEMS](state, orderItems) {
			state.orderItems = orderItems
		},

		[types.STORE_VISIT_DRAFT_ORDER](state, { orderId, orderItems, orderDeliveryDate, orderDeliveryTime, orderDeliveryPlace }) {
			state.orderId = orderId
			state.orderItems = orderItems
			state.orderDeliveryDate = orderDeliveryDate
			state.orderDeliveryTime = orderDeliveryTime
			state.orderDeliveryPlace = orderDeliveryPlace
		},

		[types.STORE_VISIT_DRAFT_ORDER_DELIVERY_DATE](state, orderDeliveryDate) {
			state.orderDeliveryDate = orderDeliveryDate
		},

		[types.STORE_VISIT_DRAFT_ORDER_DELIVERY_TIME](state, orderDeliveryTime) {
			state.orderDeliveryTime = orderDeliveryTime
		},

		[types.STORE_VISIT_DRAFT_ORDER_DELIVERY_PLACE](state, orderDeliveryPlace) {
			state.orderDeliveryPlace = orderDeliveryPlace
		},

		[types.STORE_VISIT_DRAFT_MARKETING](state, marketing) {
			state.marketing = marketing
		},

		[types.STORE_VISIT_DRAFT_PAYMENTS](state, payments) {
			state.payments = payments
		},

		[types.STORE_VISIT_DRAFT_DISCOUNT](state, discountAmount) {
			state.discountAmount = discountAmount
		},

		[types.STORE_VISIT_DRAFT_FROM_VISIT](state, payload) {
			state.id = payload.id
			state.customerTypeId = payload.customerTypeId
			state.entityId = payload.entityId
			state.entityName = payload.entityName
			state.visitDate = payload.visitDate
			state.priceTypeId = payload.priceTypeId
			state.durationInMinutes = payload.durationInMinutes
			state.invoiceItems = payload.invoiceItems
			state.returnItems = payload.returnItems
			state.originalMaterialAmounts = payload.originalMaterialAmounts
			state.marketing = payload.marketing
			state.payments = payload.payments
			// a server visit replaces the draft outright - any order left over from an
			// abandoned draft must not follow along into this visit
			state.orderId = null
			state.orderItems = []
			state.orderDeliveryDate = null
			state.orderDeliveryTime = null
			state.orderDeliveryPlace = null
		},

		[types.RESET_VISIT_DRAFT](state) {
			state.id = null
			state.customerTypeId = null
			state.entityId = null
			state.entityName = null
			state.visitDate = null
			state.priceTypeId = null
			state.durationInMinutes = null
			state.invoiceItems = []
			state.returnItems = []
			state.orderId = null
			state.orderItems = []
			state.orderDeliveryDate = null
			state.orderDeliveryTime = null
			state.orderDeliveryPlace = null
			state.marketing = defaultMarketing()
			state.payments = []
			state.discountAmount = null
			state.originalMaterialAmounts = {}
		}
	},

	getters: {
		getMaterialsTotal: state => state.invoiceItems.reduce((res, { data }) => {
			const priceNet = (Number(data.amount) || 0) * (Number(data.price) || 0)
			return res + (priceNet - ((Number(data.discount) || 0) / 100) * priceNet)
		}, 0),

		getReturnsTotal: state => state.returnItems.reduce((res, { data }) => {
			return res + ((Number(data.amount) || 0) * (Number(data.price) || 0))
		}, 0),

		getFinalTotal: (state, getters) =>
			getters.getMaterialsTotal - (Number(state.discountAmount) || 0)
	}
}
